import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import '../Dashboard/matchup.css';
import { Alert, Row, Col } from 'react-bootstrap';
import PlayerPanel from '../Dashboard/playerPanel';
import UserHeader from '../UserHeader';
import TeamLegend from '../Dashboard/teamLegend';
import { 
  highlightColumnColor, 
  checkHealthScoreStatus, 
  sortTopSoccerPlayers,
  showHealthyToReturnDate,
  highlightTextColor
} from '../utils/dashboardCalculations';
import { formatDate, formatDateWithTime } from '../utils';
import SoccerMatchup from '../Matchup/SoccerMatchup';
import TwitterFeed from '../Team/News/TwitterFeed';
import TeamNewsFeed from '../Team/News/teamNewsFeed'; 


function SoccerTeam(props) {
	let { id: teamID } = useParams();

	useEffect(() => {
		window.scrollTo(0, 0);
		let { dispatch, healthScores, assignCurrentSport } = props;
		assignCurrentSport();
		dispatch({ type: 'VIEW_TEAM', payload: { teamID } });
		dispatch({ type: 'TEAM_SCHEDULE', payload: { teamID } });
		if (!healthScores.categoryAverages) {
			dispatch({ type: 'GET_HEALTH_SCORES', payload: { teamID } });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const [expandedGroups, setExpandedGroups] = useState({});
	const [showSchedule, handleShowSchedule] = useState(false);
	const [propID, handlePropID] = useState(null);

	const toggleGroup = (groupId) => {
		setExpandedGroups(prev => ({
			...prev,
			[groupId]: !prev[groupId]
		}));
	};

	const toggleScheduleView = (gameID) => {
		handleShowSchedule(!showSchedule);
		handlePropID(gameID);
	};

	const { user, teams, teamSchedule, healthScores } = props;
	
	if (teams.isLoading || !teams?.teamInfo || teamSchedule.isLoading || (teams && !teams.players) || healthScores.isLoading) {
		return (
			<div id='fullHeight'>
				<div id='loading' />
			</div>
		);
	}

	const teamInfo = teams && teams.teamInfo[0];
	const scheduleInfo = teamSchedule?.data?.teamInfo;
	const title = 'Health Scores for Team';
	
	if (!teamInfo) {
		return (
			<React.Fragment>
				<div className='gameMainDiv'>
					<UserHeader matchup={true} backgroundColor={'#f7f7f7'} title={title} />
					<h1 className='bold text-center'>
						Team not found or error loading team data
					</h1>
				</div>
			</React.Fragment>
		);
	}

	const playerInfo = teams && teams.players;

	// Determine if there are team tweets available (support array, object with tweets, or JSON string)
	let hasTweets = false;
	const rawTwitterInfo = teams && teams.teamTwitterInfo;
	if (Array.isArray(rawTwitterInfo)) {
		hasTweets = rawTwitterInfo.length > 0;
	} else if (rawTwitterInfo && Array.isArray(rawTwitterInfo.tweets)) {
		hasTweets = rawTwitterInfo.tweets.length > 0;
	} else if (typeof rawTwitterInfo === 'string') {
		try {
			const parsed = JSON.parse(rawTwitterInfo);
			if (Array.isArray(parsed)) hasTweets = parsed.length > 0;
			else if (parsed && Array.isArray(parsed.tweets)) hasTweets = parsed.tweets.length > 0;
		} catch (e) {
			hasTweets = false;
		}
	}
	// console.log(hasTweets, 'hasTweets')
	let topFour = playerInfo && sortTopSoccerPlayers(playerInfo, teams.topFour);
	topFour = topFour?.filter(topFour => !!topFour);

	const roster =
		playerInfo &&
		// eslint-disable-next-line array-callback-return
		playerInfo.filter((x) => {
			if (x.active === 1) {
				return x;
			}
		});

	let healthScore = teamInfo && teamInfo.team_gaming_score;
	const statusText = checkHealthScoreStatus(healthScore, false, 'team');

	const highlightColor = highlightColumnColor(healthScore, 'teamHighlight');
	const offenseDefenseDesc = "This category reflects the avg health score of all core players' injuries on a specific team. It assesses their injury impact on performance as a group.";
	if (showSchedule) {
		return (
			<React.Fragment>
				<SoccerMatchup gameID={propID} hideUserHeader={true} toggleScheduleView={() => toggleScheduleView()} />
			</React.Fragment>
		);
	}

	// Dynamic soccer positions from roster
	const positionGroups = Array.from(
		new Set(roster.map(player => player.position?.toUpperCase()))
	).filter(Boolean).map(posCode => {
		switch (posCode) {
			case 'GK':
				return { id: posCode, label: 'GOALKEEPERS' };
			case 'D':
				return { id: posCode, label: 'DEFENDERS' };
			case 'M':
				return { id: posCode, label: 'MIDFIELDERS' };
			case 'F':
				return { id: posCode, label: 'FORWARDS' };
			case 'A':
				return { id: posCode, label: 'FORWARDS' };
			default:
				return { id: posCode, label: posCode || 'UNKNOWN' };
		}
	}).sort((a, b) => {
		// Sort by position order: G, D, M, A, F, others
		const order = { 'G': 1, 'D': 2, 'M': 3, 'A': 4, 'F': 5 };
		return (order[a.id] || 99) - (order[b.id] || 99) || a.label.localeCompare(b.label);
	});

	return ( 
		<section className='gameMainSection'>
			<div className='gameMainDiv'>
				<UserHeader matchup={true} backgroundColor={'#f7f7f7'} title={title} />
				<div className='gameBoxShadow soccer'>
					<Row className='gameSpreadHeaderRow'>
						<Col lg={12} className={`gameSpreadHeaderCol ${highlightColor}`}>
							<div className='gameSpreadHeaderDiv'>
								<React.Fragment>
									<p className='gameSpreadHeaderSpread'>
										<span style={{ paddingRight: '20px', fontWeight: 'bold' }}>{statusText.status}</span>
										{healthScore}
									</p>
									<p className='gameSpreadHeaderDescription'>Team Health Score</p>
								</React.Fragment>
							</div>
						</Col>
					</Row>
					<Row className='gameTeamHeaderRow'>
						<Col lg={12} className={`gameTeamHeaderCol teamShadowHighlight col-xs-12`}>
							<div className='gameTeamHeaderDiv displayLogoTable'>
								<p className={`gameTeamHeaderSpread bold ${highlightColor}`}>{healthScore}</p>
								<div className='gameTeamHeaderLogoDiv'>
									<img src={teamInfo.team_logo !== "null" ? teamInfo.team_logo : teamInfo.team_logo_secondary} alt='Team Logo' className='gameTeamLogo' />
									<p className='gameTeamHeaderName gameTeamHeaderNameAway'>{teamInfo.team_name}</p>
								</div>
							</div>
						</Col>
					</Row>
					<TeamLegend />

					<Row className='gameMatchupRow'>
						{/* Team */}
						<Col lg={6} className='gameMatchupHeaderCol gameMatchupHeaderCol1'>
							<>
								<div className='topFourPlayerDiv'>
									<h1 className='dashboardHowItWorksHeader text-center'>Impactful Players</h1>
								</div>
								<div className='matchupHeaderDiv'>
									<Row>
										{topFour?.length>0 ?
											topFour.map((players, i) => {
												const highlightText  = highlightTextColor(players?.gaming_score, 'cardSpread', 'teamhealth', false);
												return (
													<Col lg={6} key={i}>
														<Link
															className='routerLinkMatchup hover-delay'
															title={`${players?.player_name}`}
															to={{
																pathname: `/soccer/player/${players?.player_id}`,
																playerID: players?.player_id
															}}
														>
															<div className='matchPlayerDiv'>
																<div className='matchPlayerInfoDiv matchPlayerInfoTopDiv'>
																	<p className='matchPlayerInfoNameTop'>{players?.nickname || players?.player_name}</p>
																	<p className='matchPlayerInfoInjury'>
																		<span className='grey'>POSITION:</span>
																		<span className='matchPlayerInfo'>{players?.position || players?.position_category}</span>
																	</p>
																	<p className='matchPlayerInfoInjury'>
																		<span className='grey'>HEALTH SCORE:</span>
																		<span className={`matchPlayerInfo `} style={{ color: highlightText }}>{players.gaming_score || '10'}</span>
																	</p>
																</div>
															</div>
														</Link>
													</Col>
												);
											}): null}
									</Row>
								</div>
							</>
							<>
								{/* <div className='matchupHeaderDiv'>
									<Row>
										<Col lg={12}>
											<h1 className='dashboardHowItWorksHeader'>Position Categories</h1>
										</Col>
										<Col lg={8} className='matchupHeaderCol'>
											<hr className='hrStyleMobile' />
											<p className='matchupMobileHeader'>
												{teamInfo.team_city} {teamInfo.team_name}
											</p>
											<hr className='hrStyleMobile' />
										</Col>
										<Col lg={3} className='matchupHeaderCol'>
											<p className='matchupHeader'>Health Score</p>
										</Col>
										<Col lg={1} />
									</Row>
								</div> */}
								{/* <React.Fragment>
										<div style={{ paddingBottom: '30px' }}>
										<PlayerPanel
											id={1}
											healthScoreStatus={checkHealthScoreStatus(teamInfo.team_gaming_score, roster, 'team')}
											players={roster}
											panelTitle='ALL PLAYERS'
											{{ ... }}
											openAll={showRoster}
											toggleRosterView={() => toggleRosterView()}
											sportsName='soccer'
											teamLogo={teamInfo?.team_logo !== "null" ? teamInfo?.team_logo : teamInfo?.team_logo_secondary}
											hideDropdown={true}
											ignoreCategories={true}
										/>
									</div>
								</React.Fragment> */}
								<Row>
									<Col lg={12}>
										<h1 className='dashboardHowItWorksHeader'>Position Categories</h1>
									</Col>
									<Col lg={8} className='matchupHeaderCol'>
										<hr className='hrStyleMobile' />
										<p className='matchupHeader'>{teamInfo.team_name}</p>
										<hr className='hrStyleMobile' />
									</Col>
									<Col lg={3} className='matchupHeaderCol'>
										<p className='matchupHeader'>Health Score</p>
									</Col>
									<Col lg={1} />
								</Row>
								<div style={{ paddingBottom: '30px' }}>
									{positionGroups.map(group => {
										const playersInGroup = roster.filter(
											player => player.position?.toUpperCase() === group.id
										);

										return (
										<PlayerPanel
											key={group.id}
											id={group.id}
											healthScoreStatus={checkHealthScoreStatus(
												teamInfo.team_gaming_score,
												playersInGroup,
												'team'
											)}
											players={playersInGroup}
											panelTitle={group.label}
											tooltip={offenseDefenseDesc}
											openAll={expandedGroups[group.id] || false}
											toggleRosterView={() => toggleGroup(group.id)}
											sportsName="soccer"
											teamLogo={
												teamInfo?.team_logo !== 'null'
													? teamInfo?.team_logo
													: teamInfo?.team_logo_secondary
											}
											hideDropdown={false}
											ignoreCategories={true}
										/>
										);
									})}
								</div>

							</>
							<div className='twitterFeedDiv'>
								{hasTweets ? <TwitterFeed /> : <TeamNewsFeed />}
							</div>
						</Col>

						{/* Schedules Section */}

						<Col lg={6} className='gameMatchupHeaderCol gameMatchupHeaderCol1'>
							<div className='matchupHeaderDiv'>
								<Row>
									<Col lg={12} className='matchupHeaderCol'>
									<h1 className='dashboardHowItWorksHeader text-center'>Season Schedules</h1>
									<p className='matchupMobileHeader'>
										{[teamInfo.team_city, teamInfo.team_name].filter(v => v && v !== 'null').join(' ')}
									</p>
								</Col>
									<Col lg={1} />
								</Row>
							</div>
							<div style={{ paddingBottom: '30px' }}>
								<Row>
									{scheduleInfo?.length ? (
										scheduleInfo.map((x, i) => {
											const dateObj = new Date(x.date_time);

											// Format for EST
											const estString = dateObj.toLocaleString('en-US', {
												timeZone: 'America/New_York',
												year: 'numeric',
												month: 'short',
												day: 'numeric',
												hour: '2-digit',
												minute: '2-digit',
												hour12: true,
											});

											// Format for UK
											const ukString = dateObj.toLocaleString('en-GB', {
												timeZone: 'Europe/London',
												year: 'numeric',
												month: 'short',
												day: 'numeric',
												hour: '2-digit',
												minute: '2-digit',
												hour12: true,
											});

											const isHome = teamInfo.team_id === x.home_team_id;
											const opponentName = isHome ? `${x.away_team_city} ${x.away_team_name}` : `${x.home_team_city} ${x.home_team_name}`;
											const homeAwayTag = isHome ? '(H)' : '(A)';

											return (
												<React.Fragment key={i}>
													<Col lg={6} className='scheduleLinkCol'>
														<div className='scheduleLinkDiv'>
															<div className='scheduleLink hover-delay'>
																<p className='scheduleLinkTeam'>
																	<span className='bold'>
																		{teamInfo.team_id !== x.away_team_id ? x.away_team_name : x.home_team_name}
																	</span>
																	<br />
																	<span style={{ fontSize: '14px', display: 'block', marginTop: '0' }}>
																		{estString} (EST)
																		<br />
																		{ukString} (UK)
																	</span>
																</p>
															</div>
														</div>
													</Col>
												</React.Fragment>
											);
										})
									) : (
										<h1>No Team Schedules Found</h1>
									)}
								</Row>
							</div>
						</Col>
					</Row>
				</div>
			</div>
		</section>
	);
}

const mapStateToProps = (state) => ({
	isLoading: state.isLoading,
	isLoaded: state.isLoaded,
	healthScores: state.healthScores,
	teams: state.teams,
	teamSchedule: state.teamSchedule,
	user: state.user
});

export default connect(mapStateToProps)(SoccerTeam);
